import {
  CopyOutlined,
  DislikeOutlined,
  EllipsisOutlined,
  LikeOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import clsx from "clsx";
import { useCallback, type MouseEvent, type ReactNode } from "react";
import "./BChatItem.scss";

export type BChatItemRole = "user" | "assistant";
export type BChatItemStatus = "done" | "loading" | "streaming";

export type BChatItemProps = {
  role: BChatItemRole;
  content?: string;
  status?: BChatItemStatus;
  onCopy?: () => void;
  onLike?: () => void;
  onDislike?: () => void;
  onShare?: () => void;
  onMore?: () => void;
  className?: string;
};

type ContentSegment =
  | { type: "text"; value: string }
  | { type: "code"; language: string; value: string };

const FENCE_REGEX = /```([^\n`]*)\n([\s\S]*?)```/g;
const INLINE_MD_REGEX = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g;

const parseContent = (content: string): ContentSegment[] => {
  const segments: ContentSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  FENCE_REGEX.lastIndex = 0;
  while ((match = FENCE_REGEX.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", value: content.slice(lastIndex, match.index) });
    }
    segments.push({
      type: "code",
      language: (match[1] || "code").trim() || "code",
      value: match[2].replace(/\n$/, ""),
    });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({ type: "text", value: content.slice(lastIndex) });
  }

  return segments;
};

const renderInlineMarkdown = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  INLINE_MD_REGEX.lastIndex = 0;
  while ((match = INLINE_MD_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];
    if (token.startsWith("`") && token.endsWith("`")) {
      nodes.push(
        <code key={`md-${key++}`} className="b-chat-item__inline-code">
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith("**") && token.endsWith("**")) {
      nodes.push(<strong key={`md-${key++}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("*") && token.endsWith("*")) {
      nodes.push(<em key={`md-${key++}`}>{token.slice(1, -1)}</em>);
    } else {
      nodes.push(token);
    }

    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
};

const writeClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // ignore clipboard errors in unsupported environments
  }
};

type CodeBlockProps = {
  language: string;
  value: string;
};

const CodeBlock = ({ language, value }: CodeBlockProps) => {
  const handleCopyCode = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      void writeClipboard(value);
    },
    [value],
  );

  return (
    <div className="b-chat-item__code">
      <div className="b-chat-item__code-header">
        <span className="b-chat-item__code-lang">
          <span className="b-chat-item__code-icon">&lt;/&gt;</span>
          {language}
        </span>
        <button
          type="button"
          className="b-chat-item__code-copy"
          aria-label="Sao chép mã"
          onClick={handleCopyCode}
        >
          <CopyOutlined />
        </button>
      </div>
      <pre className="b-chat-item__code-body">
        <code>{value}</code>
      </pre>
    </div>
  );
};

const BChatItem = ({
  role,
  content = "",
  status = "done",
  onCopy,
  onLike,
  onDislike,
  onShare,
  onMore,
  className,
}: BChatItemProps) => {
  const effectiveStatus = role === "user" ? "done" : status;
  const showLoading = role === "assistant" && effectiveStatus === "loading";
  const showActions = role === "assistant" && effectiveStatus === "done" && content.length > 0;
  const showContent =
    !showLoading && (content.length > 0 || effectiveStatus === "streaming");

  const handleCopy = useCallback(() => {
    void writeClipboard(content);
    onCopy?.();
  }, [content, onCopy]);

  const handleLike = useCallback(() => {
    onLike?.();
  }, [onLike]);

  const handleDislike = useCallback(() => {
    onDislike?.();
  }, [onDislike]);

  const handleShare = useCallback(() => {
    onShare?.();
  }, [onShare]);

  const handleMore = useCallback(() => {
    onMore?.();
  }, [onMore]);

  let body: ReactNode = null;

  if (showLoading) {
    body = (
      <div className="b-chat-item__loading" aria-label="Đang tải">
        <span className="b-chat-item__loading-dot" />
      </div>
    );
  } else if (showContent) {
    const segments = content.length > 0 ? parseContent(content) : [{ type: "text" as const, value: "" }];
    body = (
      <div className="b-chat-item__content">
        {segments.map((segment, index) => {
          const isLast = index === segments.length - 1;
          const caret =
            effectiveStatus === "streaming" && isLast ? (
              <span className="b-chat-item__caret" aria-hidden />
            ) : null;

          if (segment.type === "code") {
            return (
              <div key={`code-${index}`} className="b-chat-item__segment">
                <CodeBlock language={segment.language} value={segment.value} />
                {caret}
              </div>
            );
          }
          if (!segment.value && !caret) {
            return null;
          }
          return (
            <p key={`text-${index}`} className="b-chat-item__text">
              {renderInlineMarkdown(segment.value)}
              {caret}
            </p>
          );
        })}
      </div>
    );
  }

  return (
    <article
      className={clsx(
        "b-chat-item",
        `b-chat-item--${role}`,
        `b-chat-item--${effectiveStatus}`,
        className,
      )}
    >
      {role === "user" ? (
        <div className="b-chat-item__bubble">{content}</div>
      ) : (
        <>
          {body}
          {showActions ? (
            <div className="b-chat-item__actions">
              <button
                type="button"
                className="b-chat-item__action"
                aria-label="Sao chép"
                onClick={handleCopy}
              >
                <CopyOutlined />
              </button>

              {!!onLike && <button
                type="button"
                className="b-chat-item__action"
                aria-label="Thích"
                onClick={handleLike}
              >
                <LikeOutlined />
              </button>}

              {!!onDislike && <button
                type="button"
                className="b-chat-item__action"
                aria-label="Không thích"
                onClick={handleDislike}
              >
                <DislikeOutlined />
              </button>}


              {!!onShare && <button
                type="button"
                className="b-chat-item__action"
                aria-label="Chia sẻ"
                onClick={handleShare}
              >
                <ShareAltOutlined />
              </button>}

              {!!onMore && <button
                type="button"
                className="b-chat-item__action"
                aria-label="Thêm"
                onClick={handleMore}
              >
                <EllipsisOutlined />
              </button>}

            </div>
          ) : null}
        </>
      )}
    </article>
  );
};

export default BChatItem;
